/**
 * §23.5 — Isı haritasının SAF çizim/ölçek çekirdeği.
 *
 * Burada DOM, React ve ağ YOKTUR: renk skalası, gauss lekesi, normalizasyon,
 * kümülatif scroll erişimi ve sahne ölçüsü `Heatmap.tsx`'ten bağımsız test
 * edilebilsin diye ayrıldı (`heatmap-render.test.ts`, `replay-engine.ts` emsali).
 * Sayfa yalnızca "ne çizilecek" kararını buradan alır; "nereye çizilecek"
 * (canvas/iframe DOM'u) `Heatmap.tsx`'te kalır.
 *
 * §21.2 — çizim ELLE yazılır: gauss çekirdeği de renk rampası da kütüphanesizdir;
 * panelin çizim katmanına tek bir runtime bağımlılığı eklenmez.
 */

/* -------------------------------------------------------------------------- */
/* Sabitler ve tipler                                                          */
/* -------------------------------------------------------------------------- */

/** §23.2 — `HEATMAP_GRID`: harita 100×100 hücredir (grid_x/grid_y 0..99). */
export const HEATMAP_GRID = 100;

/** §23.3 — `SCROLL_BUCKET_PCT = 5` → 20 dilim (bucket 0..19). */
export const SCROLL_BUCKETS = 20;

/** §23.5 — tıklama lekesinin yarıçapı: 12 CSS pikseli. */
export const BLOB_RADIUS = 12;

/** §23.5 — panel toggle'ı yalnız bu ikisini gösterir (tablet API'de var, toggle'da yok). */
export type HeatmapDevice = 'desktop' | 'mobile';

/** §23.4 `HeatmapResponse.grid` satırı — YALNIZ c>0 hücreler gelir. */
export interface GridCell {
  x: number;
  y: number;
  c: number;
}

/** Normalize edilmiş hücre: `t` 0..1 aralığında yoğunluk (1 = en sıcak hücre). */
export interface NormalizedCell {
  x: number;
  y: number;
  t: number;
}

/** §23.4 `HeatmapResponse.scroll` satırı. */
export interface ScrollBucket {
  bucket: number;
  c: number;
}

/** §23.5 scroll bandı: `pct` = "%X kullanıcı buraya ulaştı" (kümülatif). */
export interface ScrollReachBand {
  bucket: number;
  pct: number;
}

/** Canvas'a yazılabilir renk: kanal başına 0..255, alfa 0..1. */
export type Rgba = [number, number, number, number];

/** Sanal sahne ölçüsü — iframe + canvas konteynerinin CSS piksel boyutu. */
export interface StageSize {
  w: number;
  h: number;
}

/* -------------------------------------------------------------------------- */
/* Renk skalası (§23.5)                                                        */
/* -------------------------------------------------------------------------- */

interface ColorStop {
  at: number;
  rgba: Rgba;
}

/**
 * §23.5 renk durakları: 0 → şeffaf, 0.25 → mavi `#2B6CB0`, 0.60 → sarı `#ECC94B`,
 * 1.0 → kırmızı `#E53E3E`; aralar lineer interpolasyondur.
 *
 * "0 şeffaf" durağı SİYAH değil, ALFASI 0 MAVİ olarak yazıldı. Gerekçe: renk
 * kanalları düz (premultiply edilmemiş) interpolasyona girdiği için `[0,0,0,0]`
 * durağı 0–0.25 aralığında lekenin kenarını lacivert-siyaha çalan kirli bir tona
 * çevirirdi; alfası 0 mavi ile aralık saf "mavi solması" olur.
 */
const SCALE: readonly ColorStop[] = [
  { at: 0, rgba: [43, 108, 176, 0] }, // #2B6CB0, tamamen şeffaf
  { at: 0.25, rgba: [43, 108, 176, 1] }, // #2B6CB0
  { at: 0.6, rgba: [236, 201, 75, 1] }, // #ECC94B
  { at: 1, rgba: [229, 62, 62, 1] }, // #E53E3E
];

/** Sonlu olmayan girdiyi de 0..1'e indirir — harita asla NaN piksel üretmez. */
function clamp01(value: number): number {
  if (!Number.isFinite(value)) return 0;
  if (value <= 0) return 0;
  if (value >= 1) return 1;
  return value;
}

/**
 * §23.5 — yoğunluk (0..1) → renk. Duraklar arası LİNEER interpolasyon.
 *
 * `t` kıskaçlanır: 0 altı şeffaf, 1 üstü kırmızı olur; çağıranın normalize
 * etmeyi unutması haritayı bozmaz.
 */
export function colorFor(t: number): Rgba {
  const v = clamp01(t);

  // v'yi kapsayan [i-1, i] aralığını bul (son aralıkta kal: v = 1 kenarı).
  let i = 1;
  while (i < SCALE.length - 1 && v > (SCALE[i] as ColorStop).at) i += 1;

  const lo = SCALE[i - 1] as ColorStop;
  const hi = SCALE[i] as ColorStop;
  const span = hi.at - lo.at;
  const k = span <= 0 ? 1 : (v - lo.at) / span;

  return [
    Math.round(lo.rgba[0] + (hi.rgba[0] - lo.rgba[0]) * k),
    Math.round(lo.rgba[1] + (hi.rgba[1] - lo.rgba[1]) * k),
    Math.round(lo.rgba[2] + (hi.rgba[2] - lo.rgba[2]) * k),
    // Alfa 0..1 kalır (canvas'ta 255'e, CSS'te rgba()'ya çevrilir); kayan nokta
    // artıklarını testlerde kovalamamak için 3 haneye yuvarlanır.
    Math.round((lo.rgba[3] + (hi.rgba[3] - lo.rgba[3]) * k) * 1000) / 1000,
  ];
}

/** CSS `rgba(...)` metni — scroll bantlarının zemin rengi (§23.5). */
export function cssColorFor(t: number): string {
  const [r, g, b, a] = colorFor(t);
  return `rgba(${r}, ${g}, ${b}, ${a})`;
}

/* -------------------------------------------------------------------------- */
/* Gauss lekesi (§23.5, elle — §21.2)                                          */
/* -------------------------------------------------------------------------- */

/** Çekirdek kenar uzunluğu: yarıçapın iki yanı + merkez. */
export function kernelSize(radius: number): number {
  return 2 * Math.max(0, Math.floor(radius)) + 1;
}

/**
 * §23.5 — yarıçapı `radius` olan gauss lekesi; `(2r+1)²` uzunluğunda satır-öncelikli
 * ağırlık matrisi. Merkez 1.0'dır, yarıçapın DIŞI tam 0'dır.
 *
 * σ = r/2 seçildi: kenarda ağırlık `e^-2 ≈ 0.135` kalır — leke yumuşak biter ama
 * yarıçap görünür bir sınır olarak durur. Dairesel kıskaç (d > r → 0) kare
 * çekirdeğin köşelerinde kalan tortuyu siler; onsuz yoğun haritada lekeler
 * birbirine kare desende yapışırdı.
 *
 * Kütüphane YOK (§21.2): `Math.exp` dışında hiçbir şeye dayanmaz.
 */
export function gaussKernel(radius: number): Float32Array {
  const r = Math.max(0, Math.floor(Number.isFinite(radius) ? radius : 0));
  const size = 2 * r + 1;
  const kernel = new Float32Array(size * size);

  if (r === 0) {
    kernel[0] = 1;
    return kernel;
  }

  const sigma = r / 2;
  const twoSigmaSq = 2 * sigma * sigma;

  for (let dy = -r; dy <= r; dy += 1) {
    for (let dx = -r; dx <= r; dx += 1) {
      const distSq = dx * dx + dy * dy;
      const index = (dy + r) * size + (dx + r);
      // Yarıçap dışı sıfır — bkz. yukarıdaki dairesel kıskaç gerekçesi.
      kernel[index] = distSq > r * r ? 0 : Math.exp(-distSq / twoSigmaSq);
    }
  }

  return kernel;
}

/* -------------------------------------------------------------------------- */
/* Normalizasyon ve ölçek                                                      */
/* -------------------------------------------------------------------------- */

/**
 * §23.5 — "alfa birikimi maksimum hücreye normalize edilir": hücre sayıları
 * en yoğun hücreye bölünerek 0..1 yoğunluğa çevrilir.
 *
 * Boş dizi ya da tüm sayılar 0 ise HEPSİ 0 döner (sıfıra bölme yok). Negatif/
 * bozuk sayaçlar 0'a kıskaçlanır; ızgara dışı hücreler (x/y ∉ 0..99) atılır —
 * tek bozuk satır tüm haritanın ölçeğini kaydırmamalıdır.
 */
export function normalize(grid: readonly GridCell[]): NormalizedCell[] {
  const cells: GridCell[] = [];
  let max = 0;

  for (const cell of grid) {
    const x = Math.floor(cell.x);
    const y = Math.floor(cell.y);
    if (!Number.isFinite(x) || !Number.isFinite(y)) continue;
    if (x < 0 || x >= HEATMAP_GRID || y < 0 || y >= HEATMAP_GRID) continue;
    const c = Number.isFinite(cell.c) && cell.c > 0 ? cell.c : 0;
    cells.push({ x, y, c });
    if (c > max) max = c;
  }

  if (max <= 0) return cells.map((cell) => ({ x: cell.x, y: cell.y, t: 0 }));
  return cells.map((cell) => ({ x: cell.x, y: cell.y, t: cell.c / max }));
}

/**
 * §23.5 KÜMÜLATİF erişim: `reach_pct(b) = 100 × Σ(c, bucket ≥ b) / Σ(c)`.
 *
 * Her zaman 20 bandın TAMAMI döner (veri seyrek olsa da panel 20 bant çizer).
 * Dilim 0 tanım gereği %100'dür (her page_leave en az 0. dilime ulaşmıştır);
 * toplam 0 ise sıfıra bölmek yerine hepsi 0 döner.
 */
export function scrollReach(scroll: readonly ScrollBucket[]): ScrollReachBand[] {
  const counts = new Array<number>(SCROLL_BUCKETS).fill(0);
  let total = 0;

  for (const row of scroll) {
    const bucket = Math.floor(row.bucket);
    if (!Number.isFinite(bucket) || bucket < 0 || bucket >= SCROLL_BUCKETS) continue;
    const c = Number.isFinite(row.c) && row.c > 0 ? row.c : 0;
    // Aynı dilim birden çok satırla gelebilir (gün kırılımı toplanmadan geldiyse).
    counts[bucket] = (counts[bucket] as number) + c;
    total += c;
  }

  const bands: ScrollReachBand[] = [];
  let atOrBelow = 0; // b'den KÜÇÜK dilimlerin toplamı — kuyruk toplamını çıkarmayla bulur
  for (let b = 0; b < SCROLL_BUCKETS; b += 1) {
    const tail = total - atOrBelow; // Σ(c, bucket ≥ b)
    const pct = total <= 0 ? 0 : (100 * tail) / total;
    bands.push({ bucket: b, pct: Math.round(pct * 10) / 10 });
    atOrBelow += counts[b] as number;
  }

  return bands;
}

/** §23.5 — sahne genişliği cihaza SABİTTİR; §37.2 ekran görüntüleri de bu genişliklerde alınır. */
export function stageWidth(device: HeatmapDevice): number {
  return device === 'mobile' ? 390 : 1280;
}

/** §23.5 — gerçek yükseklik bilinmediğinde kullanılan sanal oran. */
export const VIRTUAL_HEIGHT_RATIO = 3;

/**
 * Sahne ölçüsü.
 *
 * §23.5 (MVP): genişlik cihaza sabit, yükseklik genişliğin 3 KATI. Gerekçe
 * cross-origin iframe'in `scrollHeight`'ının OKUNAMAMASIYDI — sabit oran
 * deterministikti ama hizalama YAKLAŞIKTI.
 *
 * §37.3 (M21): ekran görüntüsü varsa gerçek yükseklik BİLİNİR ve `background`
 * ile gelir. O zaman 3 kat varsayımı KULLANILMAZ; sahne, görüntünün kendi
 * en-boy oranına göre ölçeklenir ve hizalama varsayıma değil ÖLÇÜME oturur.
 *
 * Görüntü genişliği sahne genişliğinden farklı olabilir (deviceScaleFactor,
 * kaydırma çubuğu): bu yüzden yükseklik ORANLA taşınır, doğrudan kopyalanmaz.
 */
export function stageSize(
  device: HeatmapDevice,
  background?: { width: number; height: number } | null,
): StageSize {
  const w = stageWidth(device);
  if (
    background &&
    Number.isFinite(background.width) &&
    Number.isFinite(background.height) &&
    background.width > 0 &&
    background.height > 0
  ) {
    return { w, h: Math.round((background.height / background.width) * w) };
  }
  return { w, h: w * VIRTUAL_HEIGHT_RATIO };
}

/**
 * Hücre merkezinin sahne içindeki CSS piksel konumu — leke tam hücre ortasına
 * oturur (§23.5 "hücre merkezine gauss lekesi").
 */
export function cellCenter(x: number, y: number, stage: StageSize): { cx: number; cy: number } {
  return {
    cx: ((x + 0.5) * stage.w) / HEATMAP_GRID,
    cy: ((y + 0.5) * stage.h) / HEATMAP_GRID,
  };
}

/* -------------------------------------------------------------------------- */
/* Piksel üretimi                                                              */
/* -------------------------------------------------------------------------- */

/** Renk rampası LUT'u: piksel başına `colorFor` çağırmak milyonlarca dizi ayırırdı. */
const RAMP_STEPS = 256;
const RAMP: Uint8ClampedArray = (() => {
  const lut = new Uint8ClampedArray(RAMP_STEPS * 4);
  for (let i = 0; i < RAMP_STEPS; i += 1) {
    const [r, g, b, a] = colorFor(i / (RAMP_STEPS - 1));
    lut[i * 4] = r;
    lut[i * 4 + 1] = g;
    lut[i * 4 + 2] = b;
    lut[i * 4 + 3] = Math.round(a * 255);
  }
  return lut;
})();

/**
 * §23.5 tıklama modu — hücrelerden RGBA piksel tamponu üretir (canvas'a
 * `putImageData` ile basılır; bu fonksiyon DOM'a DOKUNMAZ).
 *
 * Akış: hücreler maksimuma normalize edilir (§23.5) → her hücrenin merkezine
 * `t` ağırlıklı gauss lekesi BİRİKTİRİLİR → biriken tampon kendi tepesine
 * normalize edilip renk rampasından geçirilir. İkinci normalizasyon şart:
 * üst üste binen lekeler 1'i aşabilir, ham değerle rampanın kırmızı ucu geniş
 * bir alana yayılıp haritayı okunamaz hâle getirirdi.
 */
export function renderHeatPixels(
  grid: readonly GridCell[],
  stage: StageSize,
  radius: number = BLOB_RADIUS,
): Uint8ClampedArray {
  const w = Math.max(0, Math.floor(stage.w));
  const h = Math.max(0, Math.floor(stage.h));
  const pixels = new Uint8ClampedArray(w * h * 4);
  if (w === 0 || h === 0) return pixels;

  const r = Math.max(0, Math.floor(radius));
  const size = 2 * r + 1;
  const kernel = gaussKernel(r);

  const intensity = new Float32Array(w * h);
  let peak = 0;

  for (const cell of normalize(grid)) {
    if (cell.t <= 0) continue;
    const { cx, cy } = cellCenter(cell.x, cell.y, stage);
    const px = Math.floor(cx);
    const py = Math.floor(cy);

    for (let ky = 0; ky < size; ky += 1) {
      const y = py - r + ky;
      if (y < 0 || y >= h) continue;
      for (let kx = 0; kx < size; kx += 1) {
        const x = px - r + kx;
        if (x < 0 || x >= w) continue;
        const weight = kernel[ky * size + kx] as number;
        if (weight <= 0) continue;
        const index = y * w + x;
        const next = (intensity[index] as number) + weight * cell.t;
        intensity[index] = next;
        if (next > peak) peak = next;
      }
    }
  }

  if (peak <= 0) return pixels;

  for (let i = 0; i < intensity.length; i += 1) {
    const value = intensity[i] as number;
    if (value <= 0) continue;
    const step = Math.min(RAMP_STEPS - 1, Math.round((value / peak) * (RAMP_STEPS - 1)));
    const from = step * 4;
    const to = i * 4;
    pixels[to] = RAMP[from] as number;
    pixels[to + 1] = RAMP[from + 1] as number;
    pixels[to + 2] = RAMP[from + 2] as number;
    pixels[to + 3] = RAMP[from + 3] as number;
  }

  return pixels;
}
